import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface AsanaTask {
  gid: string;
  name: string;
  notes?: string;
  completed: boolean;
  due_on?: string;
  created_at: string;
  assignee?: {
    gid: string;
    name: string;
  };
  projects?: Array<{
    gid: string;
    name: string;
  }>;
  permalink_url?: string;
}

interface AsanaCredentials {
  apiKey: string;
  userGid: string;
}

export const useAsanaIntegration = () => {
  const [tasks, setTasks] = useState<AsanaTask[]>([]);
  const [loading, setLoading] = useState(false);
  const [credentials, setCredentials] = useState<AsanaCredentials | null>(null);
  const { toast } = useToast();

  const fetchCredentials = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      // Use edge function to fetch credentials instead of direct DB access
      const { data, error } = await supabase.functions.invoke('manage-staff-credentials', {
        body: {
          action: 'fetch'
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (error) {
        console.error('Edge function error fetching credentials:', error);
        return null;
      }

      if (!data?.success || !data?.data?.asana_api_key || !data?.data?.asana_user_gid) {
        return null;
      }

      const creds = {
        apiKey: data.data.asana_api_key,
        userGid: data.data.asana_user_gid
      };
      
      setCredentials(creds);
      return creds;
    } catch (error) {
      console.error('Error fetching Asana credentials:', error);
      return null;
    }
  }, []);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const creds = credentials || await fetchCredentials();
      if (!creds) {
        toast({
          title: "Setup Required",
          description: "Please configure your Asana API key and User GID in Profile Settings",
          variant: "destructive",
        });
        return [];
      }

      console.log('Fetching Asana tasks with credentials:', { userGid: creds.userGid });
      
      const { data, error } = await supabase.functions.invoke('asana-tasks', {
        body: { 
          apiKey: creds.apiKey,
          userGid: creds.userGid
        }
      });

      if (error) throw error;

      if (data?.success && data?.tasks) {
        setTasks(data.tasks);
        return data.tasks;
      } else {
        throw new Error(data?.error || 'Failed to fetch tasks');
      }
    } catch (error) {
      console.error('Error fetching Asana tasks:', error);
      toast({
        title: "Error",
        description: `Failed to fetch Asana tasks: ${error.message}`,
        variant: "destructive",
      });
      return [];
    } finally {
      setLoading(false);
    }
  }, [credentials, fetchCredentials, toast]);

  const updateTask = useCallback(async (taskGid: string, updates: Partial<AsanaTask>) => {
    try {
      const creds = credentials || await fetchCredentials();
      if (!creds) return false;

      const { data, error } = await supabase.functions.invoke('asana-tasks', {
        body: { 
          action: 'update',
          apiKey: creds.apiKey,
          taskGid,
          updates
        }
      });

      if (error) throw error;

      if (data?.success) {
        // Refresh tasks to get updated data
        await fetchTasks();
        toast({
          title: "Success",
          description: "Task updated successfully",
        });
        return true;
      } else {
        throw new Error(data?.error || 'Failed to update task');
      }
    } catch (error) {
      console.error('Error updating task:', error);
      toast({
        title: "Error",
        description: `Failed to update task: ${error.message}`,
        variant: "destructive",
      });
      return false;
    }
  }, [credentials, fetchCredentials, fetchTasks, toast]);

  return {
    tasks,
    loading,
    credentials,
    fetchCredentials,
    fetchTasks,
    updateTask
  };
};